/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  FormControl,
  Grid,
  Box,
  IconButton,
  Alert,
  Tooltip,
  Autocomplete,
  Typography,
  TextField,
  FormLabel,
  Chip,
  // Button,
  List,
  ListItem,
} from '@mui/material';
import ERROR_TYPES from 'modules/common/constants/error-types';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { selectNotification } from 'modules/common/notifications/selectors';
import { Trash, X, Save } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { FieldArray, Formik } from 'formik';
import { Modal } from 'modules/common/components';
import { visualActions } from 'modules/visual/slice';
import { TAG_LIST } from 'modules/visual/constants';
import { updateVisualValidation } from 'modules/visual/validation/visual-form-validation';
import { selectVisualByID } from 'modules/visual/selectors';
import { isEmpty } from 'modules/common/helpers/object';
import { DeleteOutlined } from '@mui/icons-material';
//
const FormStyles = { borderRadius: 10 };
/**
 * Form to update visual
 * @param {*} param0
 * @returns
 */
const UpdateVisualForm = ({ show, isShow, visualId }) => {
  const dispatch = useDispatch();
  //
  const organizationId = useSelector(selectOrganizationId);
  const notification = useSelector(selectNotification);
  const visual = useSelector(selectVisualByID);
  //
  const [isDelete, setDelete] = useState(false);
  const [isUpdate, setUpdate] = useState(false);
  const [initialValues, setInitialValues] = useState({
    name: '',
    pageName: '',
    pageDisplayName: '',
    reportId: '',
    reportName: '',
    tags: [],
    order: '',
    datasetId: '',
    description: '',
    agentGuidance: [],
    currentGuidance: '',
    currentTags: [],
  });
  const formElement = useRef(null);
  //
  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      isShow(false);
    }
  }, [notification]);
  //
  useEffect(() => {
    if (!isEmpty(visual)) {
      setInitialValues({
        name: visual.name,
        pageName: visual.pageName,
        pageDisplayName: visual.pageDisplayName,
        reportId: visual.reportId,
        reportName: visual.reportName,
        tags: visual.tags,
        order: visual.order,
        datasetId: visual.datasetId,
        description: visual.description ?? '',
        agentGuidance: visual?.agentGuidance ?? [],
      });
    }
  }, [visual]);
  //
  const deleteVisual = (payload) => {
    dispatch(visualActions.deleteVisual(payload));
    setDelete(false);
  };
  //
  const onSubmitUpdateVisual = async (values) => {
    const valueObj = {
      name: values.name,
      order: values.order,
      reportId: values.reportId,
      pageName: values.pageName,
      tags: values.tags,
      description: values.description ?? '',
      agentGuidance: values.agentGuidance,
    };
    dispatch(visualActions.updateVisual({ ...valueObj, organizationId, visualId }));
  };
  //
  return (
    <Box
      xs={12}
      md={4}
      ml={4}
      mt={2}
      component={Grid}
      container
      item
      display="flex"
      direction="column"
      sx={{ backgroundColor: 'white', flexShrink: 0, display: show ? 'flex' : 'none' }}
    >
      Update Visual
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={updateVisualValidation}
        onSubmit={onSubmitUpdateVisual}
      >
        {({
          errors,
          dirty,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          handleReset,
          isSubmitting,
          touched,
          values,
          // setFieldError,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid container direction="row" justifyContent="space-between" px={2} mb={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {visual?.name}
              </Typography>
              <Grid>
                <Tooltip title="Update">
                  <IconButton
                    disabled={isSubmitting}
                    onClick={() =>
                      errors.name || errors.order || errors.tags ? '' : setUpdate(true)
                    }
                    color="inherit"
                    size="small"
                  >
                    <Save />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton color="error" size="small" onClick={() => setDelete(true)}>
                    <Trash />
                  </IconButton>
                </Tooltip>
                <IconButton color="inherit" size="small" onClick={() => isShow(false)}>
                  <X />
                </IconButton>
              </Grid>
            </Grid>
            <Grid container spacing={2} direction="column">
              {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                <Alert mt={2} mb={3} severity={notification?.type}>
                  {notification?.message}
                </Alert>
              )}
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="name"
                    value={values.name}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    my={2}
                    label="Visual Name"
                    size="small"
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="reportId"
                    value={values.reportId}
                    error={Boolean(touched.reportId && errors.reportId)}
                    helperText={touched.reportId && errors.reportId}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    my={2}
                    label="Report ID"
                    size="small"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="reportName"
                    value={values.reportName}
                    error={Boolean(touched.reportName && errors.reportName)}
                    helperText={touched.reportName && errors.reportName}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    my={2}
                    label="Report Name"
                    size="small"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="pageName"
                    value={values.pageName}
                    error={Boolean(touched.pageName && errors.pageName)}
                    helperText={touched.pageName && errors.pageName}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    my={2}
                    label="Page Name"
                    size="small"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="pageDisplayName"
                    value={values.pageDisplayName}
                    error={Boolean(touched.pageDisplayName && errors.pageDisplayName)}
                    helperText={touched.pageDisplayName && errors.pageDisplayName}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    my={2}
                    label="Page Display Name"
                    size="small"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  {!isEmpty(visual) && (
                    <Autocomplete
                      size="small"
                      value={values.tags}
                      fullWidth
                      multiple
                      disableClearable
                      disableCloseOnSelect
                      name="tags"
                      limitTags={3}
                      isOptionEqualToValue={(option, value) => option === value}
                      options={TAG_LIST || []}
                      onChange={(e, value) => setFieldValue('tags', value)}
                      onBlur={handleBlur}
                      renderInput={(params) => (
                        <TextField
                          name="tags"
                          error={Boolean(errors.tags)}
                          helperText={errors.tags}
                          label="Tags"
                          size="small"
                          {...params}
                          fullWidth
                        />
                      )}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          padding: 0,
                          height: 'fit-content',
                        },
                        marginY: 2,
                      }}
                    />
                  )}
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <TextField
                  type="number"
                  name="order"
                  value={values.order}
                  error={Boolean(touched.order && errors.order)}
                  helperText={touched.order && errors.order}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  fullWidth
                  label="Order"
                  my={2}
                  size="small"
                />
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="datasetId"
                    value={values.datasetId}
                    error={Boolean(touched.datasetId && errors.datasetId)}
                    helperText={touched.datasetId && errors.datasetId}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    my={2}
                    label="Dataset ID"
                    size="small"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="description"
                    value={values.description}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    fullWidth
                    label="Description"
                    size="small"
                    multiline
                    minRows={4}
                    maxRows={10}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={2}>
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider', // Uses the theme's standard divider color
                    borderRadius: 1,
                    minHeight: '100px',
                    backgroundColor: '#fafafa', // Light grey background to make it "pop"
                  }}
                >
                  <FormLabel>Agent Guidance</FormLabel>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    {/* Guidance Input */}

                    {/* <TextField
                      fullWidth
                      name="currentGuidance"
                      label="Prompt"
                      value={values.currentGuidance}
                      onChange={(e) => setFieldValue('currentGuidance', e.target.value)}
                      error={touched.currentGuidance && Boolean(errors.currentGuidance)}
                      helperText={touched.currentGuidance && errors.currentGuidance}
                    /> */}

                    {/* Tags Input (Autocomplete with Chips) */}
                    {/* <Autocomplete
                      small
                      multiple
                      // freeSolo
                      fullWidth
                      limitTags={3}
                      options={['semantic_audit_trend', 'interpreter_agent', 'tour_agent']}
                      value={values.currentTags}
                      onChange={(e, newValue) => setFieldValue('currentTags', newValue)}
                      renderTags={(value, getTagProps) =>
                        value?.map((option, index) => (
                          <Chip label={option} size="small" {...getTagProps({ index })} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Tags"
                          placeholder="Press Enter to add"
                          size="small"
                        />
                      )}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          padding: 0,
                          height: 'fit-content',
                        },
                      }}
                    /> */}

                    {/* <Button
                      variant="outlined"
                      sx={{ py: 2 }}
                      disabled={!values.currentGuidance || values.currentGuidance.trim() === ''}
                      onClick={() => {
                        // 2. Manual Validation Logic
                        if (!values.currentGuidance || values.currentGuidance.trim() === '') {
                          setFieldError('currentGuidance', 'Prompt is required to add an entry');
                          return;
                        }

                        const newEntry = {
                          prompt: values.currentGuidance,
                          tags: values.currentTags,
                        };

                        // Add to start of list
                        setFieldValue('agentGuidance', [newEntry, ...values.agentGuidance]);

                        // 3. Reset temporary fields (and clear manual errors)
                        setFieldValue('currentGuidance', '');
                        setFieldValue('currentTags', []);
                        setFieldError('currentGuidance', undefined);
                      }}
                    >
                      Add
                    </Button> */}
                  </Box>

                  {/* Displaying the Added Entries */}
                  <FieldArray name="agentGuidance">
                    {({ remove }) => (
                      <List>
                        {values.agentGuidance?.map((item, index) => (
                          <ListItem
                            // eslint-disable-next-line react/no-array-index-key, no-underscore-dangle
                            key={index}
                            secondaryAction={
                              <IconButton color="error" edge="end" onClick={() => remove(index)}>
                                <DeleteOutlined />
                              </IconButton>
                            }
                            sx={{
                              borderBottom: '1px solid #eee',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                            }}
                          >
                            <FormLabel>{item.prompt}</FormLabel>
                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {item.tags?.map((tag, tIdx) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Chip key={tIdx} label={tag} size="small" />
                              ))}
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </FieldArray>
                </Box>
              </Grid>
              <Modal
                open={isDelete}
                handleClose={() => setDelete(false)}
                title="Delete Visual"
                content="Are you sure you want to delete the visual?"
                handleSuccess={() => deleteVisual({ organizationId, id: visual?.id })}
                closeLabel="Cancel"
                successLabel="Delete"
                variant="contained"
                color="error"
              />
              <Modal
                open={isUpdate}
                handleClose={() => {
                  if (dirty) {
                    handleReset();
                  }
                  setUpdate(false);
                }}
                title="Update Visual"
                content="Are you sure you want to update the visual ?"
                handleSuccess={() => {
                  formElement.current?.click();
                  setUpdate(false);
                }}
                closeLabel="Cancel"
                successLabel="Update"
                variant="contained"
                color="primary"
              />
            </Grid>
            <input ref={formElement} style={{ visibility: 'hidden' }} type="submit" />
          </form>
        )}
      </Formik>
    </Box>
  );
};
//
export default UpdateVisualForm;
